import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";

type Status = "idle" | "pending" | "done" | "failed";
const STORAGE_KEY = "vira.activeJob";

interface PollState {
  status: Status;
  stage: string | null;
  resultUrl: string | null;
  error: string | null;
}

/** Polls a generation job every ~3s; recovers an in-flight job across reloads. */
export function useJobPoller() {
  const [state, setState] = useState<PollState>({
    status: "idle",
    stage: null,
    resultUrl: null,
    error: null,
  });
  const timer = useRef<ReturnType<typeof setInterval> | null>(null);

  const stop = useCallback(() => {
    if (timer.current) clearInterval(timer.current);
    timer.current = null;
  }, []);

  const poll = useCallback(
    async (jobId: string) => {
      try {
        const r = await api.pollJob(jobId);
        setState({
          status: r.status,
          stage: r.stage,
          resultUrl: r.result_url,
          error: r.error,
        });
        if (r.status === "done" || r.status === "failed") {
          stop();
          localStorage.removeItem(STORAGE_KEY);
        }
      } catch (err) {
        setState((s) => ({ ...s, error: err instanceof Error ? err.message : "error" }));
      }
    },
    [stop],
  );

  const track = useCallback(
    (jobId: string) => {
      localStorage.setItem(STORAGE_KEY, jobId);
      setState({ status: "pending", stage: "Building prompt…", resultUrl: null, error: null });
      stop();
      void poll(jobId);
      timer.current = setInterval(() => poll(jobId), 3000);
    },
    [poll, stop],
  );

  const reset = useCallback(() => {
    stop();
    localStorage.removeItem(STORAGE_KEY);
    setState({ status: "idle", stage: null, resultUrl: null, error: null });
  }, [stop]);

  // Recover across reloads (PRD §7.3).
  useEffect(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) track(saved);
    return stop;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { ...state, track, reset };
}
